import type { ReactNode, Ref } from 'react';
import { Children, createContext, isValidElement, useContext, useMemo, useState } from 'react';
import { composeRenderProps } from 'react-aria-components/composeRenderProps';
import type { ContextValue } from 'react-aria-components/slots';
import type { TooltipProps as AriaTooltipProps, TooltipTriggerComponentProps } from 'react-aria-components/Tooltip';
import { Tooltip as AriaTooltip, TooltipTrigger as AriaTooltipTrigger } from 'react-aria-components/Tooltip';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';

import { Button } from './Button';
import { IconButton } from './IconButton';
import type { TooltipTriggerContextValue } from './Perceivable';
import { DisabledTooltipTriggerContext, IsTooltipTriggerContext, TooltipTriggerContext } from './Perceivable';
import type { PopoverProps } from './Popover';
import { popoverStyles } from './Popover';
import { Switch } from './Switch';
import { ToggleButton } from './ToggleButton';
import { ToggleIconButton } from './ToggleIconButton';
import { useLPContextProps } from './utils';

import styles from './styles/Tooltip.module.css';

interface TooltipProps extends AriaTooltipProps, VariantProps<typeof tooltipStyles> {
	ref?: Ref<HTMLDivElement>;
	width?: PopoverProps['width'];
}
interface TooltipTriggerProps extends TooltipTriggerComponentProps {}

const TooltipContext = createContext<ContextValue<TooltipProps, HTMLDivElement>>(null);

const tooltipStyles = cva(styles.base, {
	variants: {
		variant: {
			default: styles.tooltip,
			popover: popoverStyles({ width: 'default' }),
		},
	},
	defaultVariants: {
		variant: 'default',
	},
});

/**
 * A tooltip displays a description of an element on hover or focus.
 *
 * https://react-spectrum.adobe.com/react-aria/Tooltip.html
 */
const Tooltip = ({ ref, ...props }: TooltipProps) => {
	const [mergedProps, mergedRef] = useLPContextProps(props, ref, TooltipContext);
	const { variant = 'default', width = 'default' } = mergedProps;
	const anchor = useContext(TooltipTriggerContext)?.anchor;
	const anchorRef = useMemo(() => (anchor ? { current: anchor } : null), [anchor]);

	return (
		<AriaTooltip
			data-theme={variant === 'default' ? 'dark' : undefined}
			offset={4}
			crossOffset={0}
			{...mergedProps}
			{...(anchorRef && { triggerRef: anchorRef })}
			ref={mergedRef}
			className={composeRenderProps(mergedProps.className, (className, renderProps) =>
				variant === 'popover'
					? tooltipStyles({
							...renderProps,
							variant: null,
							className: popoverStyles({ width, className }),
						})
					: tooltipStyles({ ...renderProps, variant, className }),
			)}
			data-trigger={variant === 'popover' ? 'DialogTrigger' : undefined}
		/>
	);
};

const disabledTriggerTypes = new Set<unknown>([Button, IconButton, Switch, ToggleButton, ToggleIconButton]);

const markDisabledTrigger = (children: ReactNode) =>
	Children.map(children, (child) =>
		isValidElement(child) && disabledTriggerTypes.has(child.type) ? (
			<IsTooltipTriggerContext.Provider value>{child}</IsTooltipTriggerContext.Provider>
		) : (
			child
		),
	);

/**
 * Shows a `Tooltip` when its trigger is hovered or focused.
 *
 * A disabled `Button`, `IconButton`, `ToggleButton`, `ToggleIconButton` or `Switch` placed directly in a
 * `TooltipTrigger` stays focusable and hoverable so the tooltip can explain why it is disabled. Buttons are
 * announced with `aria-disabled` and a switch as read-only; all of them ignore presses, key presses and form
 * submission. Controls nested deeper, for example inside a wrapper element or a custom `Pressable`, stay
 * natively disabled. This only applies below a `DisabledTooltipTriggerContext` set to `true`.
 *
 * @example
 * ```tsx
 * <TooltipTrigger>
 *   <Button isDisabled>Save</Button>
 *   <Tooltip>Make a change to save</Tooltip>
 * </TooltipTrigger>
 * ```
 */
const TooltipTrigger = (props: TooltipTriggerProps) => {
	const { delay = 500, closeDelay = 250 } = props;
	const [anchor, setAnchor] = useState<Element | null>(null);
	const triggerContext = useMemo<TooltipTriggerContextValue>(() => ({ anchor, setAnchor }), [anchor]);
	const isDisabledTriggerEnabled = useContext(DisabledTooltipTriggerContext) && !props.isDisabled;
	return (
		<TooltipTriggerContext.Provider value={props.isDisabled ? null : triggerContext}>
			<AriaTooltipTrigger delay={delay} closeDelay={closeDelay} {...props}>
				{isDisabledTriggerEnabled ? markDisabledTrigger(props.children) : props.children}
			</AriaTooltipTrigger>
		</TooltipTriggerContext.Provider>
	);
};

export { Tooltip, TooltipContext, TooltipTrigger, tooltipStyles };
export type { TooltipProps, TooltipTriggerProps };
